import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { passwordStrengthValidator } from './password-validation.utils';

export { getPasswordRequirements, passwordStrengthValidator } from './password-validation.utils';

export type RegisterProfileType = 'particular' | 'empresa';

type CreateRegisterFormOptions = {
  includeProfileType?: boolean;
  defaultProfileType?: RegisterProfileType;
};

export function passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
  const password = control.get('password');
  const confirmPassword = control.get('passwordConfirmation');

  if (!password || !confirmPassword) {
    return null;
  }

  if (password.value !== confirmPassword.value) {
    confirmPassword.setErrors({ ...(confirmPassword.errors || {}), passwordMismatch: true });
    return { passwordMismatch: true };
  }

  if (confirmPassword.errors?.['passwordMismatch']) {
    const { passwordMismatch, ...restErrors } = confirmPassword.errors;
    void passwordMismatch;
    confirmPassword.setErrors(Object.keys(restErrors).length ? restErrors : null);
  }

  return null;
}

export function createRegisterForm(
  formBuilder: FormBuilder,
  options: CreateRegisterFormOptions = {},
): FormGroup {
  const {
    includeProfileType = false,
    defaultProfileType = 'empresa',
  } = options;

  const controls: Record<string, any> = {
    name: ['', Validators.required],
    lastName: [''],
    email: ['', [Validators.required, Validators.email]],
    phone: ['', [Validators.required, Validators.pattern(/^\+?[0-9\s\-\(\)]{6,20}$/)]],
    companyName: ['', Validators.required],
    nif: ['', Validators.required],
    password: ['', [Validators.required, passwordStrengthValidator()]],
    passwordConfirmation: ['', Validators.required],
    acceptTerms: [false, Validators.requiredTrue],
  };

  if (includeProfileType) {
    controls['profileType'] = [defaultProfileType, Validators.required];
  }

  const form = formBuilder.group(controls, {
    validators: [passwordMatchValidator],
  });

  updateRegisterCompanyNameValidation(form);
  return form;
}

export function updateRegisterCompanyNameValidation(form: FormGroup): void {
  const profileType = (form.get('profileType')?.value || 'empresa') as RegisterProfileType;
  const companyNameControl = form.get('companyName');

  if (!companyNameControl) {
    return;
  }

  if (profileType === 'empresa') {
    companyNameControl.setValidators([Validators.required]);
  } else {
    companyNameControl.clearValidators();
    companyNameControl.setValue('');
  }

  companyNameControl.updateValueAndValidity();
}

export function buildRegisterPayload(form: FormGroup, isCompany: boolean): any {
  const lastName = form.get('lastName')?.value || '';
  const firstName = form.get('name')?.value || '';
  const fullName = lastName ? `${firstName} ${lastName}` : firstName;
  const email = form.get('email')?.value || '';

  const registerData: any = {
    phone: form.get('phone')?.value || '',
    nif: form.get('nif')?.value || '',
    name: fullName,
    last_name: lastName,
    email,
    password: form.get('password')?.value,
    password_confirmation: form.get('passwordConfirmation')?.value,
    is_company: isCompany,
  };

  if (isCompany && form.get('companyName')?.value) {
    registerData.company_name = form.get('companyName')?.value;
    registerData.company = { email };
  }

  return registerData;
}

export function extractRegisteredCompanyId(registerResponse: any): number | null {
  return registerResponse?.company?.id
    || registerResponse?.company_id
    || registerResponse?.data?.company_id
    || registerResponse?.id
    || null;
}
